/*
 * ॐ नमः शिवाय
 * ಓಂ ನಮಃ ಶಿವಾಯ
 * ఓం నమః శివాయ
 * ஓம் நம சிவாய
 *
 * Copyright (c) 2025 Rudra Coresoft Technologies Private Limited
 *
 * This software is licensed under the VIJAYA 1.0 License.
 *
 * You are free to use, modify, and distribute this software under the terms 
 * of the VIJAYA 1.0 License.
 *
 * MANDATORY ATTRIBUTION:
 * 1. The attribution to Rudra Coresoft Technologies Private Limited in this 
 *    copyright notice must not be removed even after modifications.
 * 2. For Free License usage, the footer phrases "Powered by arcee.io" and 
 *    "Developed by Rudra Coresoft" (plus logos) must remain visible.
 *
 * See the LICENSE file for the full text.
 */

/**
 * TrainingCard
 * ------------
 * "Your training": this week's total against last week, the last 12 weeks as
 * columns with the current week in orange, and a dashed line at the 12-week
 * average. Distance / Time switches the measure. `compact` is the phone/tablet
 * version shown above the feed when the right-hand rail is hidden.
 *
 * Every number comes from the athlete summary (useAthleteSummary): the weekly
 * totals, the average and calories from athlete_weekly_training, the streak
 * and the day strip from athlete_active_days — computed in SQL, one request.
 */

import React, { useMemo, useState } from 'react';
import FeedIcon from './FeedIcon';
import {
  weekDays, currentStreak, formatHoursMinutes, niceScale, weekRange, startOfWeek, dayLetter, monthShort,
} from '../../pages/feed/feedModel';

export default function TrainingCard({ summary, isLoading, now = new Date(), compact = false, nextEvent }) {
  const [measure, setMeasure] = useState('km');
  const weeks = summary?.weeks || [];
  const activeDays = summary?.activeDays;
  const days = useMemo(() => weekDays(activeDays || [], now), [activeDays, now]);
  const streak = useMemo(() => currentStreak(activeDays || [], now), [activeDays, now]);

  if (isLoading || !summary) {
    return <div className={`fd-skel${compact ? ' fd-compact' : ''}`} style={{ height: compact ? 120 : 470, borderRadius: 22 }} aria-hidden="true" />;
  }
  if (weeks.length === 0) return null;

  const isKm = measure === 'km';
  const pick = (w) => (isKm ? w.km : w.sec / 3600);
  const show = (v) => (isKm ? v.toFixed(1) : formatHoursMinutes(Math.round(v * 3600)));
  const vals = weeks.map(pick);
  const cur = vals[vals.length - 1];
  const prev = vals.length > 1 ? vals[vals.length - 2] : 0;
  const avg = isKm ? summary.avgKm : summary.avgSec / 3600;
  const scale = niceScale(Math.max(...vals, avg, isKm ? 10 : 1));
  const up = cur >= prev;
  const active = days.filter((d) => d.active).length;
  const kcal = weeks[weeks.length - 1].kcal;
  const hasAny = weeks.some((w) => w.count > 0);

  const chip = hasAny && (
    <span className={`fd-vs ${up ? 'up' : 'down'}`}>
      <FeedIcon name={up ? 'trending_up' : 'trending_down'} />
      {show(Math.abs(cur - prev))}{isKm ? ' km' : ''}<em>vs last week</em>
    </span>
  );

  if (compact) {
    return (
      <section className="fd-rc fd-compact" aria-label="Your training">
        <div className="cm">
          <div style={{ minWidth: 0 }}>
            <span className="fd-eyebrow">This week</span>
            <div className="fd-th-n"><b>{show(cur)}</b><span>{isKm ? 'km' : ''}</span></div>
            {chip}
          </div>
          <div className="fd-spk" role="img" aria-label={`Distance per week, last 12 weeks. This week ${show(cur)} km.`}>
            {vals.map((v, i) => <i key={weeks[i].start.getTime()} className={weeks[i].current ? 'cur' : undefined} style={{ height: `${Math.max(4, (v / scale.max) * 100)}%` }} />)}
          </div>
        </div>
        {nextEvent && (
          <div className="nxt"><FeedIcon name="calendar_month" />Next: <b>{nextEvent.title}</b> in {nextEvent.days} {nextEvent.days === 1 ? 'day' : 'days'}</div>
        )}
      </section>
    );
  }

  const ticks = [0, scale.max / 2, scale.max];
  return (
    <section className="fd-rc" aria-label="Your training">
      <div className="fd-rc-h">
        <h3>Your training</h3>
        <div className="fd-tm" role="group" aria-label="Measure">
          <button type="button" aria-pressed={isKm} onClick={() => setMeasure('km')}>Distance</button>
          <button type="button" aria-pressed={!isKm} onClick={() => setMeasure('time')}>Time</button>
        </div>
      </div>
      <div className="fd-th-n"><b>{show(cur)}</b><span>{isKm ? 'km this week' : 'moving this week'}</span></div>
      {chip}
      <div
        className="fd-tchart"
        role="img"
        aria-label={`${isKm ? 'Distance' : 'Moving time'} per week for the last 12 weeks. This week ${show(cur)}${isKm ? ' km' : ''}; 12-week average ${show(avg)}${isKm ? ' km' : ''}.`}
      >
        {ticks.map((t, i) => (
          <div key={t} className={`g${i === 0 ? ' b' : ''}`} style={{ bottom: `${(t / scale.max) * 100}%` }}>
            <span>{isKm ? t : `${t}h`}</span>
          </div>
        ))}
        {avg > 0 && <div className="avg" style={{ bottom: `${(avg / scale.max) * 100}%` }} />}
        <div className="fd-tcols">
          {weeks.map((w, i) => (
            <div key={w.start.getTime()} className={w.current ? 'cur' : undefined} tabIndex={0}>
              <i style={{ height: `${Math.max(2, (vals[i] / scale.max) * 100)}%` }} />
              <span className="fd-tip" role="tooltip">
                <b>{w.current ? 'This week' : `Week of ${w.start.getDate()} ${monthShort(w.start)}`}</b><br />
                {w.km.toFixed(1)} km · {formatHoursMinutes(w.sec)} · {w.count} {w.count === 1 ? 'activity' : 'activities'}
              </span>
            </div>
          ))}
        </div>
      </div>
      <div className="fd-txl" aria-hidden="true">
        {weeks.map((w, i) => (
          <span key={w.start.getTime()} className={w.current ? 'cur' : undefined}>
            {w.current ? 'Now' : i % 4 === 0 ? `${w.start.getDate()} ${monthShort(w.start)}` : ''}
          </span>
        ))}
      </div>
      <div className="fd-tlegend">
        <span><i style={{ background: 'var(--fd-accent)' }} />This week</span>
        <span><i style={{ background: 'var(--fd-bar)' }} />Past weeks</span>
        <span><i className="al" />12-wk avg <b>{show(avg)}</b></span>
      </div>
      <div className="fd-tdays" aria-label={`Active days this week: ${active}`}>
        {days.map((d) => (
          <div key={d.date.getTime()} className={d.today ? 'td' : undefined}>
            <i className={d.future ? 'fut' : d.active ? 'on' : undefined} />{dayLetter(d.date)}
          </div>
        ))}
      </div>
      <div className="fd-wfoot">
        <span>Streak <b>{streak} {streak === 1 ? 'day' : 'days'}</b></span>
        <span>Active <b>{active} {active === 1 ? 'day' : 'days'}</b></span>
        <span><b>{Math.round(kcal).toLocaleString('en-IN')}</b> kcal</span>
      </div>
      <span className="fd-eyebrow" style={{ display: 'block', marginTop: 10 }}>{weekRange(startOfWeek(now))}</span>
    </section>
  );
}
